import React, { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
export const ttsSupported = !!synth;

// Strip markdown so the spoken text sounds natural.
function plain(text = "") {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[*_#>`~|]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

// Play/pause/stop/replay for a single AI answer using the browser's speech
// synthesis. Uses the ALREADY-generated answer text — no extra AI/Gemini call.
export function SpeakButton({ text, autoPlay = false }) {
  const { language } = useLanguage();
  const [state, setState] = useState("idle"); // idle | playing | paused
  const uttRef = useRef(null);
  const startedAuto = useRef(false);

  const stop = () => { try { synth?.cancel(); } catch (e) {} setState("idle"); };

  const play = () => {
    if (!synth) return;
    try { synth.cancel(); } catch (e) {}
    const u = new SpeechSynthesisUtterance(plain(text));
    u.lang = language === "hi" ? "hi-IN" : "en-US";
    u.rate = 1; u.pitch = 1;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    uttRef.current = u;
    synth.speak(u);
    setState("playing");
  };

  const pause = () => { try { synth?.pause(); setState("paused"); } catch (e) {} };
  const resume = () => { try { synth?.resume(); setState("playing"); } catch (e) {} };

  // Voice Mode: auto-speak the finished answer once (does not create any AI call).
  useEffect(() => {
    if (autoPlay && !startedAuto.current && text) { startedAuto.current = true; play(); }
    return () => { if (autoPlay) stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, text]);

  useEffect(() => () => stop(), []);

  if (!ttsSupported) return null;

  return (
    <div className="flex items-center gap-1" data-testid="speak-controls">
      {state === "idle" && (
        <button type="button" onClick={play} data-testid="speak-play" aria-label="Play answer"
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary">
          <Volume2 className="h-4 w-4" />
        </button>
      )}
      {state === "playing" && (
        <button type="button" onClick={pause} data-testid="speak-pause" aria-label="Pause"
          className="flex h-7 w-7 items-center justify-center rounded-full text-primary transition-colors hover:bg-slate-100">
          <Pause className="h-4 w-4" />
        </button>
      )}
      {state === "paused" && (
        <button type="button" onClick={resume} data-testid="speak-resume" aria-label="Resume"
          className="flex h-7 w-7 items-center justify-center rounded-full text-primary transition-colors hover:bg-slate-100">
          <Play className="h-4 w-4" />
        </button>
      )}
      {state !== "idle" && (
        <button type="button" onClick={stop} data-testid="speak-stop" aria-label="Stop"
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-rose-500">
          <Square className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
